<script setup lang="ts">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    import { useValidation } from "@/components/forms/reusables/useValidation";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import { useStore } from "@/store";
    import { CONFIG_PROVIDE_NAME, ConfigType } from "@/store/modules/types/configModuleTypes";
    import { computed, inject, toRefs } from "vue";

    const store = useStore();

    interface Props {
        name: string;
        label?: string;
        /**
         * Samma typer av valideringsregler som Formulate (och senare Formkit)-komponenter tar emot. Används för att
         * t.ex avgöra om label ska visa *
         */
        validation?: string | Array<[rule: string, ...args: never]>;
        /**
         * Används primärt för att stödja användning av KGroup i äldre komponenter. Tanken är inte att rowGap ska
         * sättas till none i andra fall
         */
        rowGap?: "default" | "none" | "half";
        direction?: "horizontal" | "vertical";
        variant?: string;
        /**
         * Ska grid column ignoreras och istället låta innehållet flöda fritt?
         */
        freeFlow?: boolean;
        /**
         * KSub.vue prop Text som renderas under label
         */
        subText?: string;
        /**
         * KSub.vue prop Ikon som renderas under label
         */
        subIcon?: string;
        /**
         * KSub.vue prop Typ av subtitle. Default "info-dark"
         */
        subType?: string;
        /**
         * Storlek på font
         */
        size?: "default" | "large";
        /**
         * Marginal som läggs på botten av label
         */
        margin?: "default" | "large" | "none";
        shouldBeVisible?: boolean;
        /**
         * Används för att skapa en konsekvent container-stil för formulärfält.
         */
        background?: "default" | "transparent" | "blue";
    }

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    } | null>(CONFIG_PROVIDE_NAME, null);

    const props = withDefaults(defineProps<Props>(), {
        rowGap: "default",
        direction: "vertical",
        variant: "group",
        freeFlow: false,
        subType: "info-dark",
        size: "large",
        margin: "large",
        shouldBeVisible: undefined,
        background: "default",
    });

    const { name, validation, shouldBeVisible } = toRefs(props);

    const internalShouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            name: name.value,
            moduleName: configNames?.moduleName,
            subModuleName: configNames?.subModuleName,
            type: ConfigType.Group,
            overrideValue: shouldBeVisible?.value,
        })
    );

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: name.value,
        validation: validation?.value,
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });

    defineExpose({
        ...useValidationExpose,
    });
</script>

<template>
    <div v-if="internalShouldBeVisible" :class="['k-group', `gap-${rowGap}`, direction, { 'free-flow': freeFlow }]">
        <slot name="label">
            <KLabel
                v-if="label"
                :validation="modifiedValidation"
                :content="label"
                :size="size"
                :margin="margin"
                :subText="subText"
                :subIcon="subIcon"
                :subType="subType"
                :class="{ 'span-all': direction !== 'horizontal' }"
            />
        </slot>
        <div
            :class="[
                'group-content',
                'span-all',
                background === 'default' ? '' : background,
                { background: background !== 'default' },
            ]"
        >
            <slot />
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import "@/style/form";

    .k-group {
        display: grid;
        grid-template-columns: subgrid;

        .group-content {
            display: grid;
            grid-template-columns: subgrid;
            grid-auto-flow: column;

            &.background {
                padding: 12px;
                border-radius: $border-radius-pop-up;

                &.blue {
                    background-color: $color-blue-light;
                }

                &.transparent {
                    background-color: transparent;
                }
            }
        }

        &.gap-default .group-content {
            grid-row-gap: $form-row-gap;
        }

        &.gap-half .group-content {
            grid-row-gap: calc($form-row-gap / 2);
        }

        &.vertical {
            grid-auto-flow: row;

            .group-content {
                grid-auto-flow: row;
            }
        }

        &.horizontal {
            grid-auto-flow: column;
            .group-content {
                grid-auto-flow: column;
            }
        }

        &.free-flow {
            .group-content {
                grid-column: 1 / -1;
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;

                label:first-of-type {
                    width: 100%;
                }
            }

            &.gap-default .group-content {
                grid-column-gap: $form-column-gap;
                grid-row-gap: calc($form-row-gap / 2);
            }

            &.gap-half .group-content {
                grid-column-gap: calc($form-column-gap / 2);
                grid-row-gap: calc($form-row-gap / 2);
            }
        }
    }
</style>
